import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../SsnCardVerification.module.scss";
import type { SsnCardVerificationResponse } from "../../../shared/types/ssnCardVerification";
import { usePageView } from "../../../shared/analytics/usePageView";
import { trackClicked } from "../../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS } from "../../../shared/analytics/documentCaptureEvents";

interface ConfirmationScreenProps {
  verificationResult: { data: SsnCardVerificationResponse };
  onConfirm: () => void;
  onRetake: () => void;
  consumerLogo?: { src: string; alt: string };
  /**
   * This launch is the pass after the member's retake cap is reached
   * (VEX-7961). Renders the out-of-retakes variant: same fields and Confirm
   * action, but "Retake photo" is replaced by "Join a video call" — clicking
   * it still calls `onRetake` (same downstream report, `user_retake_requested`;
   * IVA already routes it to human step-up at the cap), the label is just
   * honest about what happens next instead of promising another capture.
   */
  outOfRetakes?: boolean;
}

const MASKED_SSN = "### - ## - ####";

/** Groups a 9-digit SSN as "### - ## - ####" (the Figma spec); anything else is
 *  shown as-is — OCR output is otherwise untrusted to be exactly 9 digits. */
function formatSsn(documentNumber: string): string {
  const digits = documentNumber.replace(/\D/g, "");
  if (digits.length !== 9) return documentNumber;
  return `${digits.slice(0, 3)} - ${digits.slice(3, 5)} - ${digits.slice(5)}`;
}

/** Space-separates every digit so screen readers announce the SSN digit-by-
 *  digit instead of collapsing it into a number (e.g. reading "123456789" as
 *  "one hundred twenty-three million..."), per the Figma accessibility
 *  annotations (node 4265:17351). Used only for the screen-reader-only value. */
function formatSsnForScreenReader(documentNumber: string): string {
  const digits = documentNumber.replace(/\D/g, "");
  return digits.length === 9 ? digits.split("").join(" ") : documentNumber.split("").join(" ");
}

/**
 * ConfirmationScreen — post-OCR review gate (state `confirming`), shown once the
 * backend reports a passed outcome, so a member can catch an OCR misread (e.g.
 * "5" read as "S") before it silently fails the backend's exact-match check
 * (VEX-7846). The SSN is shown by default, with a "Hide"/"Show" toggle so the
 * member can mask it (e.g. if someone's looking over their shoulder) — same
 * toggle pattern (and its accessibility treatment) already used in
 * ConfirmInfoVerification, which masks by default instead.
 *
 * Accessibility markup — 3 landmarks (header/main/footer), list structure,
 * SSN digit-by-digit announcement, button accessible names — follows the
 * Figma accessibility annotations for this screen (node 4265:17351). The
 * visible "#" mask itself is `aria-hidden` (see the SSN row below), so it's
 * never read by a screen reader despite the annotation's concern about "#".
 *
 * `outOfRetakes` (VEX-7961) swaps in the out-of-retakes variant: the pass
 * after the member's retake cap is reached, "Retake photo" no longer retakes
 * anything (IVA routes it straight to human step-up), so this variant tells
 * the truth instead — "Join a video call" — with its own heading/subtitle/
 * name-label copy. Same fields, same Confirm action, same underlying
 * `onRetake` callback either way.
 */
export function ConfirmationScreen({ verificationResult, onConfirm, onRetake, consumerLogo, outOfRetakes }: ConfirmationScreenProps) {
  const { t } = useTranslation();
  const [showSsn, setShowSsn] = useState(true);
  const headingRef = useRef<HTMLHeadingElement>(null);
  usePageView(DOCUMENT_CAPTURE_EVENTS.VIEWED.CONFIRMATION_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION });

  // This is a state change within the same document, not a real page load, so a
  // screen reader has no signal to start reading it — nothing moves focus on its
  // own. Move focus to the heading on mount so it's announced immediately (the
  // WAI-ARIA SPA-navigation pattern), then the member continues forward from there
  // through the rest of the reading order (subtitle → data → actions).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const attributes = verificationResult.data.results?.attributes;
  const fullName = [attributes?.nameFirst, attributes?.nameLast].filter(Boolean).join(" ");
  const documentNumber = attributes?.documentNumber;

  const handleConfirm = () => {
    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.CONFIRM_DOCUMENT, "button", {
      section: DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION,
      targetAction: "submit",
    });
    onConfirm();
  };

  const handleRetake = () => {
    trackClicked(
      outOfRetakes ? DOCUMENT_CAPTURE_EVENTS.CLICKED.JOIN_VIDEO_CALL : DOCUMENT_CAPTURE_EVENTS.CLICKED.RETAKE_DOCUMENT,
      "button",
      { section: DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION, targetAction: "navigate" }
    );
    onRetake();
  };

  return (
    <div className={styles.card}>
      {consumerLogo && (
        <header className={styles.cardConsumerLogoHeader}>
          <img src={consumerLogo.src} alt={consumerLogo.alt} />
          <div className={styles.cardConsumerLogoDivider} />
        </header>
      )}
      <main>
        <h1 className={styles.title} ref={headingRef} tabIndex={-1}>
          {t(outOfRetakes ? 'ssnCard.confirming.outOfRetakes.heading' : 'ssnCard.confirming.heading')}
        </h1>
        <p className={styles.subtitle}>
          {t(outOfRetakes ? 'ssnCard.confirming.outOfRetakes.subtitle' : 'ssnCard.confirming.subtitle')}
        </p>

        <ul className={styles.attributesBox}>
          {fullName && (
            <li className={styles.attributeRow}>
              <b className={styles.attributeLabel}>
                {t(outOfRetakes ? 'ssnCard.confirming.outOfRetakes.nameLabel' : 'ssnCard.confirming.fullNameLabel')}
              </b>
              <br aria-hidden="true" />
              {fullName}
            </li>
          )}
          {documentNumber && (
            <li className={styles.attributeRow}>
              <b className={styles.attributeLabel}>{t('ssnCard.confirming.ssnLabel')}</b>
              <br aria-hidden="true" />
              <div className={styles.ssnRow}>
                <span aria-hidden="true">{showSsn ? formatSsn(documentNumber) : MASKED_SSN}</span>
                {/* Screen-reader value: digit-by-digit, and aria-live="polite" so
                    revealing the SSN is announced on its own, without moving focus
                    off the toggle. Empty when hidden so the mask isn't spoken. */}
                <span className={styles.srOnly} aria-live="polite">
                  {showSsn ? formatSsnForScreenReader(documentNumber) : ""}
                </span>
                <button
                  type="button"
                  className={styles.showHideBtn}
                  onClick={() => setShowSsn((v) => !v)}
                  aria-label={showSsn ? t('ssnCard.confirming.hideSsnAriaLabel') : t('ssnCard.confirming.showSsnAriaLabel')}
                  aria-expanded={showSsn}
                >
                  {showSsn ? t('ssnCard.confirming.hideSsn') : t('ssnCard.confirming.showSsn')}
                </button>
              </div>
            </li>
          )}
        </ul>
      </main>

      <footer>
        <button className={styles.confirmButton} onClick={handleConfirm} aria-label={t('ssnCard.confirming.confirmButtonAriaLabel')}>
          {t('ssnCard.confirming.confirmButton')}
        </button>
        <button
          className={styles.retakeLink}
          onClick={handleRetake}
          aria-label={t(outOfRetakes ? 'ssnCard.confirming.outOfRetakes.joinVideoCallAriaLabel' : 'ssnCard.confirming.retakeButtonAriaLabel')}
        >
          {t(outOfRetakes ? 'ssnCard.confirming.outOfRetakes.joinVideoCall' : 'ssnCard.confirming.retakeButton')}
        </button>
      </footer>
    </div>
  );
}
